
import { useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useSubscription } from "@/hooks/useSubscription";
import type { SubscriptionTier } from "@/types/subscription";
import { loadPayPalScript } from "@/utils/paypalScript";
import { usePayPalButton } from "@/hooks/usePayPalButton";

interface PayPalIntegrationProps {
  onLoadingChange: (isLoading: boolean) => void;
  onScriptError: (hasError: boolean) => void;
  onSubscriptionSelected?: () => void;
}

export const usePayPalIntegration = ({
  onLoadingChange,
  onScriptError,
  onSubscriptionSelected,
}: PayPalIntegrationProps) => {
  const [isLoadingPayPal, setIsLoadingPayPal] = useState(true);
  const [scriptError, setScriptError] = useState(false);
  const { setSelectedTier } = useSubscription();
  const paypalScriptLoaded = useRef(false);
  const scriptLoadAttempted = useRef(false);
  const buttonsInitialized = useRef<{ [key: string]: boolean }>({});
  const { isInitialized, initializeButton } = usePayPalButton({
    onSuccess: onSubscriptionSelected
  });

  const handleRetry = () => {
    setScriptError(false);
    setIsLoadingPayPal(true);
    scriptLoadAttempted.current = false;
    paypalScriptLoaded.current = false;
    buttonsInitialized.current = {};
    const script = document.querySelector('script[src*="paypal.com/sdk/js"]');
    if (script) {
      script.remove();
    }
    loadPaypalScript();
  };

  const initializePayPalButton = async (tier: SubscriptionTier, clientId: string) => {
    const containerId = `paypal-button-${tier.paypalPlanId}`;
    
    if (buttonsInitialized.current[tier.paypalPlanId]) {
      console.log(`Button already initialized for tier: ${tier.name}`);
      return;
    }

    console.log('Initializing PayPal button for tier:', tier.name);
    try {
      setSelectedTier(tier);
      await initializeButton(containerId, clientId, tier);
      buttonsInitialized.current[tier.paypalPlanId] = true;
      console.log('PayPal button initialized successfully for tier:', tier.name);
    } catch (error) {
      console.error(`Failed to initialize PayPal button for ${tier.name}:`, error);
      setScriptError(true);
    }
  };

  const loadPaypalScript = async () => {
    if (scriptLoadAttempted.current) return;
    scriptLoadAttempted.current = true;

    try {
      console.log('Fetching PayPal client ID...');
      const { data, error } = await supabase.functions.invoke('get-paypal-client-id');
      
      if (error || !data?.value) {
        throw new Error(error?.message || 'PayPal client ID not found');
      }

      console.log('Loading PayPal script...');
      await loadPayPalScript(data.value);
      
      console.log('PayPal script loaded successfully');
      paypalScriptLoaded.current = true;
      onLoadingChange(false);
      setIsLoadingPayPal(false);
    } catch (error) {
      console.error('Failed to load PayPal script:', error);
      onScriptError(true);
      setScriptError(true);
      onLoadingChange(false);
      setIsLoadingPayPal(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    if (!scriptLoadAttempted.current && mounted) {
      loadPaypalScript();
    }

    return () => {
      mounted = false;
    };
  }, [onLoadingChange, onScriptError]);

  return {
    isLoadingPayPal,
    scriptError,
    initializePayPalButton,
    handleRetry,
    setIsLoadingPayPal,
    setScriptError
  };
};
